import BoldSmallText from '@/components/BoldSmallText';
import RegularText from '@/components/RegularText';
import Switch from '@/components/Switch';
import { DateRangeDirection, EnableTimelineLayout, HighlightContentPreviewId, TimelineIconStages } from '@/constants/enum';
import { optionsDateRangeDirection, optionsDateRangeIcon } from '@/constants/options';
import { timelineStages } from '@/constants/timeline-graphic';
import useClickHighlight from '@/hooks/useClickHighlight';
import ModalChangeIcon from '@/pages/Settings/Components/Appearance/components/ModalChangeIcon';
import TimelineGraphicCard from '@/pages/Settings/Components/Appearance/components/TimelineGraphicCard';
import highlightPreviewSlice, { highlightPreviewSelector } from '@/redux/slice/highlightPreview.slice';
import settingsSlice, { dataSettingsSelector } from '@/redux/slice/settings.slice';
import { Collapsible, InlineGrid, Select } from '@shopify/polaris';
import { memo, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

function TimelineGraphic() {
  const dispatch = useDispatch();
  const { handleClickHighlight } = useClickHighlight();
  const dataSettings = useSelector(dataSettingsSelector);
  const { isOrderIconClicked, isShipIconClicked, isDeliveryIconClicked } = useSelector(highlightPreviewSelector);
  const [openModal, setOpenModal] = useState(false);
  const [selectedStage, setSelectedStage] = useState<TimelineIconStages | undefined>();

  const handleChange = (key: keyof typeof dataSettings) => (value: string | number | boolean) => {
    dispatch(
      settingsSlice.actions.handleDataSettings({
        ...dataSettings,
        [key]: value,
      }),
    );
  };

  const handleOpenModal = (stage: TimelineIconStages) => {
    setOpenModal(true);
    setSelectedStage(stage);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedStage(undefined);
    if (isOrderIconClicked) {
      dispatch(highlightPreviewSlice.actions.handleClickHighlightItem({ key: 'isOrderIconClicked', value: false }));
    }
    if (isShipIconClicked) {
      dispatch(highlightPreviewSlice.actions.handleClickHighlightItem({ key: 'isShipIconClicked', value: false }));
    }
    if (isDeliveryIconClicked) {
      dispatch(highlightPreviewSlice.actions.handleClickHighlightItem({ key: 'isDeliveryIconClicked', value: false }));
    }
  };

  useEffect(() => {
    if (isOrderIconClicked) {
      handleOpenModal(TimelineIconStages.Stage1);
    }
    if (isShipIconClicked) {
      handleOpenModal(TimelineIconStages.Stage2);
    }
    if (isDeliveryIconClicked) {
      handleOpenModal(TimelineIconStages.Stage3);
    }
  }, [isOrderIconClicked, isShipIconClicked, isDeliveryIconClicked]);

  return (
    <>
      <InlineGrid columns={2}>
        <BoldSmallText>Timeline graphic</BoldSmallText>
        <div className="content-right">
          <Switch
            isActive={!!dataSettings.enable_timeline_layout}
            onSwitch={(value: boolean) => {
              dispatch(
                settingsSlice.actions.handleDataSettings({
                  ...dataSettings,
                  enable_timeline_layout: value ? EnableTimelineLayout.Active : EnableTimelineLayout.InActive,
                  timeline_delivery_step_enable: value ? true : dataSettings.timeline_delivery_step_enable,
                  timeline_ship_step_enable: value ? true : dataSettings.timeline_ship_step_enable,
                  timeline_order_step_enable: value ? true : dataSettings.timeline_order_step_enable,
                }),
              );
            }}
          />
        </div>
      </InlineGrid>

      <div className="mt-4">
        <Collapsible open={!!dataSettings.enable_timeline_layout} id="time-graphics" transition={{ duration: '500ms' }}>
          <RegularText>You can show 1 to 3 stage(s)</RegularText>
          <div className="mt-8">
            <InlineGrid columns={{ md: 1, lg: 3 }} gap={'200'}>
              {timelineStages.map((stage, index) => (
                <TimelineGraphicCard
                  key={stage.id}
                  fieldStatus={stage.fieldStatus}
                  fieldTitle={stage.fieldTitle}
                  fieldTooltip={stage.fieldTooltip}
                  stage={stage.id}
                  stageNumber={index + 1}
                  handleOpenModal={handleOpenModal}
                />
              ))}
            </InlineGrid>
          </div>
          <div className={`mt-8`}>
            <InlineGrid columns={{ xl: 2, lg: 2, md: 2, sm: 2, xs: 1 }} gap={'400'}>
              <Select
                label={<BoldSmallText>Date range direction</BoldSmallText>}
                options={optionsDateRangeDirection}
                value={dataSettings.timeline_date_range_direction?.toString()}
                onChange={(value) => {
                  handleChange('timeline_date_range_direction')(value as DateRangeDirection);
                }}
              />
              <div onClick={handleClickHighlight('isHighlightedDateRangeIcon')}>
                <Select
                  id={HighlightContentPreviewId.DateRangeIconSetup}
                  label={<BoldSmallText>Date range icon</BoldSmallText>}
                  options={optionsDateRangeIcon}
                  value={dataSettings.timeline_date_range_icon}
                  onChange={(value) => {
                    handleChange('timeline_date_range_icon')(value);
                  }}
                />
              </div>
            </InlineGrid>
          </div>
        </Collapsible>
      </div>
      <ModalChangeIcon active={openModal} onClose={handleCloseModal} stage={selectedStage} />
    </>
  );
}

export default memo(TimelineGraphic);
